/**
 * AdminLayout.jsx — admin sidebar with BeXyl brand colours.
 *
 * Mobile: hamburger opens slide-in drawer.
 * Desktop: sidebar always visible.
 *
 * TOGGLE instructions:
 * - To hide Privileges: the nav item is commented out below (search "TOGGLE: Privileges")
 * - To restore it: uncomment that one line
 */

import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/admin",           label: "Dashboard",  exact: true, icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" },
  { to: "/admin/users",     label: "Users",      exact: false, icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" },
  // TOGGLE: Privileges — comment the line below to hide from nav, uncomment to restore
  // { to: "/admin/privileges", label: "Privileges", exact: false, icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
  { to: "/admin/schemes",   label: "Schemes",    exact: false, icon: "M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" },
  { to: "/admin/documents", label: "Documents",  exact: false, icon: "M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" },
  { to: "/admin/monitor",   label: "Monitor",      exact: false, icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" },
  { to: "/catalog",         label: "View Catalog", exact: false, icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" },
];

const SIDEBAR_W = 230;

function NavIcon({ path }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [open, setOpen]  = useState(false);

  useEffect(() => {
    function onResize() { if (window.innerWidth >= 768) setOpen(false); }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  function handleLogout() { logout(); navigate("/admin/login"); }

  const sidebar = (
    <div style={{
      width: SIDEBAR_W, height: "100%",
      background: "#2d3f1a",  /* very dark green for admin */
      display: "flex", flexDirection: "column", flexShrink: 0,
      overflowY: "auto",
    }}>
      {/* Brand */}
      <div style={{
        padding: "16px 18px 14px",
        borderBottom: "1px solid rgba(212,217,128,.2)",
        background: "#cccccc",  /* light green for brand header */
        display: "flex", alignItems: "center", gap: 10,
      }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/logo.png"
            alt="BeXyl logo"
            style={{ height: 70, width: 120, objectFit: "contain" }}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          {/* Fallback SVG if logo.png not found */}
          <div style={{
            display: "none", width: 44, height: 44, borderRadius: 8,
            background: "#4B7518", alignItems: "center", justifyContent: "center",
          }}>
            <svg viewBox="0 0 44 44" width="44" height="44">
              <circle cx="16" cy="14" r="5" fill="#9BC071"/>
              <circle cx="28" cy="12" r="7" fill="#7DA72F"/>
              <ellipse cx="22" cy="28" rx="10" ry="12" fill="#775F46" opacity=".7" transform="rotate(-25,22,28)"/>
              <ellipse cx="22" cy="28" rx="10" ry="12" fill="#5C4B00" opacity=".5" transform="rotate(25,22,28)"/>
              <circle cx="22" cy="22" r="3" fill="#D4D980"/>
            </svg>
          </div>

        </div>
        {/* <div style={{
          width: 34, height: 34, borderRadius: 7,
          background: "#4B7518", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4D980" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        </div> */}
        {/* <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#D4D980",
            fontFamily: "'Nunito',sans-serif", lineHeight: 1.1 }}>BeXyl DSS</div>
          <div style={{ fontSize: 10, color: "rgba(155,192,113,.7)", lineHeight: 1 }}>
            Admin panel
          </div>
        </div> */}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, paddingTop: 8 }}>
        {NAV.map(({ to, label, exact, icon }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            onClick={() => setOpen(false)}
            className={({ isActive }) => `admin-nav-link${isActive ? " active" : ""}`}
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <NavIcon path={icon} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(212,217,128,.15)" }}>
        <div style={{ fontSize: 11, color: "rgba(155,192,113,.7)", marginBottom: 2 }}>
          {user?.name}
        </div>
        <div style={{ fontSize: 11, color: "rgba(155,192,113,.5)", marginBottom: 10 }}>
          {user?.email}
        </div>
        <a href="/" style={{
          display: "flex", alignItems: "center", gap: 5,
          fontSize: 12, color: "rgba(212,217,128,.6)", textDecoration: "none", marginBottom: 8,
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
          Public site
        </a>
        <button onClick={handleLogout} style={{
          width: "100%", fontSize: 12, color: "rgba(212,217,128,.7)",
          background: "none", border: "1px solid rgba(212,217,128,.25)",
          borderRadius: 6, padding: "6px 0", cursor: "pointer",
          fontFamily: "'Nunito',sans-serif",
        }}>
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>

      {/* Desktop sidebar — sticky so it stays visible while main content scrolls */}
      <div className="d-none d-md-flex" style={{
        flexShrink: 0,
        position: "sticky",
        top: 0,
        height: "100vh",
        alignSelf: "flex-start",
      }}>
        {sidebar}
      </div>

      {/* Mobile overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
            zIndex: 1040,
          }}
        />
      )}

      {/* Mobile drawer */}
      <div className="d-md-none" style={{
        position: "fixed", top: 0, left: 0, height: "100vh",
        zIndex: 1050,
        transform: open ? "translateX(0)" : `translateX(-${SIDEBAR_W}px)`,
        transition: "transform .25s ease",
      }}>
        {sidebar}
      </div>

      {/* Main content */}
      <main style={{ flex: 1, background: "#F6F9F2", minWidth: 0, overflowX: "auto" }}>

        {/* Mobile top bar */}
        <div className="d-flex d-md-none" style={{
          alignItems: "center", gap: 12, padding: "10px 16px",
          background: "#2d3f1a", borderBottom: "1px solid rgba(212,217,128,.2)",
        }}>
          <button
            onClick={() => setOpen(v => !v)}
            aria-label="Toggle navigation"
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "#D4D980", padding: 4,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <rect y="3"  width="22" height="2.5" rx="1.25" fill="currentColor"/>
              <rect y="10" width="22" height="2.5" rx="1.25" fill="currentColor"/>
              <rect y="17" width="22" height="2.5" rx="1.25" fill="currentColor"/>
            </svg>
          </button>
          {/* <span style={{ color: "#D4D980", fontSize: 14, fontWeight: 700,
            fontFamily: "'Nunito',sans-serif" }}>
            BeXyl DSS — Admin
          </span> */}
        </div>

        {children}
      </main>
    </div>
  );
}
